import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TranslationCacheService } from './translation-cache.service';

/**
 * SimplifiedTranslateLoader - Core modules loader with centralized caching
 *
 * Purpose:
 * - Load ONLY core modules immediately on app start
 * - Use TranslationCacheService for caching
 * - Page modules are loaded by RouteTranslationResolver on demand
 *
 * Core Modules (always loaded):
 * - auth: Authentication & authorization
 * - shared: Shared components
 * - common: Common UI elements (buttons, labels)
 * - validation: Validation messages
 * - header: Header menu items
 * - enums: Status enums and constants
 * - dispatcher: Legacy dispatcher feature (flat structure)
 *
 * Page Modules (lazy loaded via RouteTranslationResolver):
 * - users, drivers, cars, companies, orders, etc.
 */
@Injectable()
export class SimplifiedTranslateLoader implements TranslateLoader {

  // Core modules loaded immediately
  private readonly CORE_MODULES = [
    'auth',
    'shared',
    'common',
    'roles',
    'validation',
    'header',
    'enums',
    'dispatcher',
    'dashboard',  // Dashboard uses keys without 'pages.' prefix
    'documents',  // Documents shared component (documents.*)
    'pages/chat', // Chat translations (pages.chat.*)
    'telephony',  // Telephony panel — floating widget, visible on all routes
    'pages/tariffs', // Tariff field labels used inside the dispatcher order-creation form (tariffs.*)
  ];

  constructor(
    private http: HttpClient,
    private cache: TranslationCacheService
  ) {}

  /**
   * Load core modules for specified language
   */
  getTranslation(lang: string): Observable<any> {

    // Ensure language is supported
    const supportedLangs = ['en', 'uk', 'lv', 'ru'];
    const finalLang = supportedLangs.includes(lang) ? lang : 'en';

    const cacheKey = `${finalLang}_core`;

    // Check cache first
    const cached$ = this.cache.get(cacheKey);
    if (cached$) {
      return cached$;
    }

    // Load all core modules in parallel
    const requests = this.CORE_MODULES.map(module => {
      const path = `./assets/i18n/${finalLang}/${module}.json`;

      return this.http.get(path).pipe(
        catchError(error => {
          return of({});
        })
      );
    });

    const request$ = forkJoin(requests).pipe(
      map((results) => {

        // Combine all core modules into single object
        const combined: any = {};

        this.CORE_MODULES.forEach((module, index) => {
          const moduleData: any = results[index] || {};

          // Some modules are fetched from assets/i18n/{lang}/pages/<name>.json but their
          // translation keys are exposed flat, without the 'pages/' path segment
          // (e.g. 'pages/chat' -> 'chat.*', 'pages/tariffs' -> 'tariffs.*'), so templates
          // can reference them as `'chat.errors.title' | translate` etc.
          const keyPrefix = module.startsWith('pages/') ? module.slice('pages/'.length) : module;

          // Flatten module data WITH module prefix
          Object.keys(moduleData).forEach(key => {
            const value = moduleData[key];

            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
              // Nested structure - flatten it with module prefix
              const flattened = this.flattenObject(value, `${keyPrefix}.${key}`);
              Object.assign(combined, flattened);
            } else {
              // Simple key-value - add module prefix
              combined[`${keyPrefix}.${key}`] = value;
            }
          });
        });

        return combined;
      })
    );

    // Store in cache
    this.cache.set(cacheKey, request$);

    return request$;
  }

  /**
   * Flatten nested object to dot notation
   */
  private flattenObject(obj: any, prefix: string = ''): any {
    const result: any = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          Object.assign(result, this.flattenObject(obj[key], newKey));
        } else {
          result[newKey] = obj[key];
        }
      }
    }

    return result;
  }
}
